/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module image/image/converters
 */
import { first } from 'ckeditor5/src/utils';
/**
 * Returns a function that converts the image view representation:
 *
 *		<figure class="image"><img src="..." alt="..."></img></figure>
 *
 * to the model representation:
 *
 *		<imageBlock src="..." alt="..."></imageBlock>
 *
 * The entire content of the `<figure>` element except the first `<img>` is being converted as children
 * of the `<imageBlock>` model element.
 *
 * @param {module:image/imageutils~ImageUtils} imageUtils
 * @returns {Function}
 */

export function viewFigureToModel(imageUtils) {
  return function (dispatcher) {
    dispatcher.on('element:figure', converter);
  };

  function converter(evt, data, conversionApi) {
    // Do not convert if this is not an "image figure".
    if (!conversionApi.consumable.test(data.viewItem, {
      name: true,
      classes: 'image'
    })) {
      return;
    } // Find an image element inside the figure element.


    var viewImage = imageUtils.getViewImageFromWidget(data.viewItem); // Do not convert if image element is absent, is missing src attribute or was already converted.

    if (!viewImage || !viewImage.hasAttribute('src') || !conversionApi.consumable.test(viewImage, {
      name: true
    })) {
      return;
    } // Convert view image to model image.


    var conversionResult = conversionApi.convertItem(viewImage, data.modelCursor); // Get image element from conversion result.

    var modelImage = first(conversionResult.modelRange.getItems()); // When image wasn't successfully converted then finish conversion.

    if (!modelImage) {
      return;
    } // Convert rest of the figure element's children as an image children.


    conversionApi.convertChildren(data.viewItem, modelImage);
    conversionApi.updateConversionResult(modelImage, data);
  }
}
/**
 * Converter used to convert the `srcset` model image attribute to the `srcset`, `sizes` and `width` attributes in the view.
 *
 * @param {module:image/imageutils~ImageUtils} imageUtils
 * @param {'imageBlock'|'imageInline'} imageType The type of the image.
 * @returns {Function}
 */

export function srcsetAttributeConverter(imageUtils, imageType) {
  return function (dispatcher) {
    dispatcher.on("attribute:srcset:".concat(imageType), converter);
  };

  function converter(evt, data, conversionApi) {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }

    var writer = conversionApi.writer;
    var element = conversionApi.mapper.toViewElement(data.item);
    var img = imageUtils.getViewImageFromWidget(element);

    if (data.attributeNewValue === null) {
      var srcset = data.attributeOldValue;

      if (srcset.data) {
        writer.removeAttribute('srcset', img);
        writer.removeAttribute('sizes', img);

        if (srcset.width) {
          writer.removeAttribute('width', img);
        }
      }
    } else {
      var _srcset = data.attributeNewValue;

      if (_srcset.data) {
        writer.setAttribute('srcset', _srcset.data, img); // Always outputting `100vw`. See https://github.com/ckeditor/ckeditor5-image/issues/2.

        writer.setAttribute('sizes', '100vw', img);

        if (_srcset.width) {
          writer.setAttribute('width', _srcset.width, img);
        }
      }
    }
  }
}
/**
 * Converter used to convert a given image attribute from the model to the view.
 *
 * @param {module:image/imageutils~ImageUtils} imageUtils
 * @param {'imageBlock'|'imageInline'} imageType The type of the image.
 * @param {String} attributeKey The name of the attribute to convert.
 * @returns {Function}
 */

export function modelToViewAttributeConverter(imageUtils, imageType, attributeKey) {
  return function (dispatcher) {
    dispatcher.on("attribute:".concat(attributeKey, ":").concat(imageType), converter);
  };

  function converter(evt, data, conversionApi) {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }

    var viewWriter = conversionApi.writer;
    var element = conversionApi.mapper.toViewElement(data.item);
    var img = imageUtils.getViewImageFromWidget(element);
    viewWriter.setAttribute(data.attributeKey, data.attributeNewValue || '', img);
  }
}